import { FacilityDetailsResponse } from "@features/ax/assetRegistry/facilities/types";
import { StatLabelWithTooltip } from "@features/ax/shared/StatLabelWithTooltip";
import { logEvent } from "@shared/analytics/analytics";
import { GenericTable } from "@shared/components/GenericTable";
import { PageHeader } from "@shared/components/PageHeader";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Tabs } from "@shared/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { AlertTriangle, DollarSign, Info } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useFacilityFinance } from "../../../../hooks";

interface FacilityFinanceTabProps {
  facility: FacilityDetailsResponse;
}

interface FinancialHistoryRecord {
  id: string;
  date: string;
  eventType: string;
  valueChange: string;
  bookValue: string;
  notes: string;
}

interface DepreciationItemProps {
  label: string;
  value: string | number;
  valueColor?: string;
  description?: string; // 👈 optional tooltip
}

export const DepreciationItem: React.FC<DepreciationItemProps> = ({
  label,
  value,
  valueColor = "text-gray-900",
  description,
}) => (
  <div className="flex justify-between items-center">
    <div className="flex items-center gap-1">
      <span className="text-sm font-medium text-gray-500">{label}:</span>
      {description && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info
                size={14}
                className="text-muted-foreground cursor-pointer hover:text-gray-600"
              />
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p>{description}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
    <span className={`text-base font-semibold ${valueColor}`}>{value}</span>
  </div>
);

export const FacilityFinanceTab: React.FC<FacilityFinanceTabProps> = ({
  facility,
}) => {
  const [historyView, setHistoryView] = useState("all-years");
  const [eventType, setEventType] = useState("all-events");
  const currency = useCurrency();

  const { data: financeData } = useFacilityFinance(facility.id);
  useEffect(() => {
    logEvent("finance_tab_viewed");
  }, []);
  const endOfLifeAssets = useMemo(() => {
    if (!financeData?.system?.assetsNearingEndOfLife) return [];

    return financeData?.system?.assetsNearingEndOfLife.map((asset) => ({
      id: asset.assetId,
      name: asset.assetName,
      remainingLife: asset.remainingLife,
      currentBookValue: asset.currentBookValue,
      estReplacementCost: asset.replacementCost,
      color: asset.color,
    }));
  }, [financeData?.system?.assetsNearingEndOfLife]);

  const financialHistory: FinancialHistoryRecord[] = [];

  const getRemainingLifeColor = (asset: any) => {
    if (asset.color) {
      switch (asset.color.toLowerCase()) {
        case "red":
          return "bg-red-100 text-red-800";
        case "orange":
          return "bg-orange-100 text-orange-800";
        case "yellow":
          return "bg-yellow-100 text-yellow-800";
        case "green":
          return "bg-green-100 text-green-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    }

    const years = parseInt(asset.remainingLife);
    if (years <= 2) return "bg-red-100 text-red-800";
    if (years <= 3) return "bg-orange-100 text-orange-800";
    return "bg-yellow-100 text-yellow-800";
  };

  const endOfLifeColumns: TableColumn<any>[] = [
    {
      key: "name",
      header: "Asset Name",
      type: "text",
      render: (value, record) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help text-blue-600 hover:underline">
                {value}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-sm">
              This asset is nearing its end-of-life. Its replacement will
              require an estimated capital expenditure of{" "}
              <span className="font-semibold">
                {record.estimatedReplacementCost?.toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })}
              </span>
              . This information is critical for long-term capital planning.
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
    {
      key: "remainingLife",
      header: "Remaining Life",
      type: "custom",
      render: (value: string, row: any) => (
        <Badge className={getRemainingLifeColor(row)}>{value}</Badge>
      ),
    },
    {
      key: "currentBookValue",
      header: "Current Book Value",
      type: "text",
      sortable: true,
    },
    {
      key: "estReplacementCost",
      header: "Est. Replacement Cost",
      type: "text",
      sortable: true,
      render: (value: string) => <span className="font-medium">{value}</span>,
    },
  ];

  const financialHistoryColumns: TableColumn<FinancialHistoryRecord>[] = [
    {
      key: "date",
      header: "Effective Date",
      type: "text",
      sortable: true,
      headerTooltip: "The date the financial event was recorded.",
    },
    {
      key: "eventType",
      header: "Event Type",
      type: "text",
      sortable: true,
      headerTooltip:
        "The nature of the event (e.g., Annual Depreciation, Capital Improvement).",
    },
    {
      key: "valueChange",
      header: "Value Change",
      type: "custom",
      render: (value: string) => (
        <span
          className={value.startsWith("-") ? "text-red-600" : "text-green-600"}
        >
          {value}
        </span>
      ),
      headerTooltip:
        "The amount by which the book value changed due to this event.",
    },
    {
      key: "bookValue",
      header: "Book Value at End of Period",
      type: "custom",
      render: (value: string) => <span className="font-medium">{value}</span>,
      headerTooltip: "The resulting book value after this event occurred.",
    },
    {
      key: "notes",
      header: "Notes",
      type: "custom",
      render: (value: string) => <span className="text-gray-600">{value}</span>,
      headerTooltip: "A brief, human-readable description of the event.",
    },
  ];

  const OverviewTab = () => {
    if (!financeData) {
      return (
        <div className="text-center py-12">
          <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No financial data available</p>
        </div>
      );
    }

    const schedule = financeData?.system?.facilityDepreciationSchedule;

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Facility Depreciation Schedule</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4">
              <DepreciationItem
                label="Annual Depreciation (Current Year)"
                value={`${currency} ${schedule?.annualDepreciation}`}
                description="The calculated depreciation expense for this facility for the current fiscal year."
              />
              <DepreciationItem
                label="Depreciation To Date (Current Year)"
                value={`${currency} ${schedule?.depreciationToDate}`}
                description="The portion of this year's depreciation accounted for as of today's date."
              />
              <DepreciationItem
                label="Remaining Depreciation"
                value={`${currency} ${schedule?.remainingDepreciation}`}
                description="The amount of value yet to be depreciated over the facility's remaining life."
              />
              <DepreciationItem
                label="Next Depreciation Date"
                value={schedule?.nextDepreciationDate}
                description="The date the next annual depreciation expense will be officially recorded."
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <div>
                <CardTitle>Assets Nearing End-of-Life</CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  Assets with less than 5 years of useful life remaining
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <GenericTable
              data={endOfLifeAssets}
              columns={endOfLifeColumns}
              rowKey="id"
              emptyMessage="No assets nearing end-of-life"
            />
          </CardContent>
        </Card>
      </div>
    );
  };

  const HistoryTab: React.FC<{ facilityId: number }> = ({ facilityId }) => {
    // const [searchParams, setSearchParams] = useSearchParams();
    // const [currentPage, setCurrentPage] = useState(1);
    // const [isExporting, setIsExporting] = useState(false);
    // const itemsPerPage = 10;

    // // Extract filters from URL
    // const currentEventType = searchParams.get("eventType") || "all-events";
    // const currentView = searchParams.get("historyView") || "all-years";
    // const currentSearch = decodeURIComponent(searchParams.get("search") || "");

    // const { data: historyResponse } = useFacilityFinanceHistory(
    //   facilityId,
    //   currentEventType,
    //   currentView,
    //   currentSearch,
    //   currentPage,
    //   itemsPerPage
    // );

    // const currentFilters = useMemo(
    //   () => ({
    //     eventType: currentEventType,
    //     historyView: currentView,
    //     search: currentSearch,
    //   }),
    //   [currentEventType, currentView, currentSearch]
    // );

    // const displayData = useMemo(
    //   () => historyResponse?.result || [],
    //   [historyResponse]
    // );

    // const pagination = useMemo(() => {
    //   const totalItems = historyResponse?.count || displayData.length;
    //   const totalPages = Math.ceil(totalItems / itemsPerPage);
    //   return {
    //     currentPage,
    //     totalPages,
    //     totalItems,
    //     itemsPerPage,
    //     startItem: totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0,
    //     endItem: Math.min(currentPage * itemsPerPage, totalItems),
    //   };
    // }, [currentPage, historyResponse?.count, displayData.length]);

    // const handlePageChange = (page: number) => {
    //   const newParams = {
    //     ...Object.fromEntries(searchParams.entries()),
    //     page: page.toString(),
    //   };
    //   setSearchParams(newParams);
    //   setCurrentPage(page);
    // };

    // const handleApplyFilters = (filters: typeof currentFilters) => {
    //   const newParams: any = { ...Object.fromEntries(searchParams.entries()) };
    //   if (filters.eventType && filters.eventType !== "all-events")
    //     newParams.eventType = filters.eventType;
    //   else delete newParams.eventType;
    //   if (filters.historyView && filters.historyView !== "all-years")
    //     newParams.historyView = filters.historyView;
    //   else delete newParams.historyView;
    //   if (filters.search?.trim())
    //     newParams.search = encodeURIComponent(filters.search.trim());
    //   else delete newParams.search;
    //   newParams.page = "1";
    //   setSearchParams(newParams);
    //   setCurrentPage(1);
    // };

    // const handleResetFilters = () => {
    //   const newParams: any = { ...Object.fromEntries(searchParams.entries()) };
    //   delete newParams.eventType;
    //   delete newParams.historyView;
    //   delete newParams.search;
    //   newParams.page = "1";
    //   setSearchParams(newParams);
    //   setCurrentPage(1);
    // };

    // const handleExport = () => {
    //   setIsExporting(true);
    //   // implement export logic
    //   setTimeout(() => setIsExporting(false), 1000);
    // };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <PageHeader
            title="Facility Financial History"
            subtitle="Complete financial ledger showing all value changes and depreciation events"
          />
        </div>

        <GenericTable
          data={financialHistory}
          columns={financialHistoryColumns}
          rowKey="id"
          emptyMessage="No financial history records found"
        />
      </div>
    );
  };

  const financeTabComponents = {
    overview: {
      label: "Overview",
      component: <OverviewTab />,
    },
    // history: {
    //   label: "History",
    //   component: <HistoryTab facilityId={facility.id} />,
    // },
    // Removed for now will need after
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              <CardTitle>Facility Book Value</CardTitle>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-3">
            {[
              {
                key: "currentBookValue",
                label: "Current Book Value",
                description:
                  "The accounting value of the facility, calculated as initial cost minus all depreciation to date.",
                value: financeData?.system?.facilityBookValue?.currentBookValue,
                className: "text-3xl font-bold text-blue-600",
              },
              {
                key: "initialCapitalCost",
                label: "Initial Capital Cost",
                description:
                  "The original total cost to acquire or build the facility, including all initial expenses.",
                value:
                  financeData?.system?.facilityBookValue?.initialCapitalCost,
                className: "text-lg font-semibold",
              },
              {
                key: "accumulatedDepreciation",
                label: "Accumulated Depreciation",
                description:
                  "Total depreciation charged against the facility since it was placed in service.",
                value:
                  financeData?.system?.facilityBookValue
                    ?.accumulatedDepreciation,
                className: "text-lg font-semibold text-red-600",
              },
              {
                key: "salvageValue",
                label: "Salvage Value",
                description:
                  "Estimated resale value of the facility at end of its useful life; this amount is not depreciated.",
                value: financeData?.system?.facilityBookValue?.salvageValue,
                className: "text-lg font-semibold",
              },
            ].map((item) => (
              <div key={item.key}>
                <div className={item.className}>
                  {currency} {item.value}
                </div>
                <StatLabelWithTooltip
                  label={item.label}
                  description={item.description}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Tabs
        tabComponents={financeTabComponents}
        defaultValue="overview"
        urlMapping={{
          overview: "overview",
          history: "history",
        }}
        // tabsListClassName="mt-4 grid w-full grid-cols-2 mb-6"
        idPrefix="finance-tab"
        level={1}
      />
    </div>
  );
};
